import { browserName, isMobile, osName } from "react-device-detect";

const decodeJwtPayload = (token) => {
  if (!token) {
    return null;
  }

  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch (_error) {
    return null;
  }
};

// Parse browser version from userAgent or navigator APIs
const parseBrowserVersion = () => {
  if (typeof navigator === "undefined") {
    return null;
  }

  const ua = navigator.userAgent || "";

  // Chrome / Chromium
  if (/Chrome\//.test(ua)) {
    const match = ua.match(/Chrome\/([0-9.]+)/);
    if (match) return match[1];
  }

  // Firefox
  if (/Firefox\//.test(ua)) {
    const match = ua.match(/Firefox\/([0-9.]+)/);
    if (match) return match[1];
  }

  // Safari
  if (/Version\//.test(ua) && /Safari\//.test(ua)) {
    const match = ua.match(/Version\/([0-9.]+)/);
    if (match) return match[1];
  }

  // Edge (Chromium)
  if (/Edg(e|A)\//.test(ua)) {
    const match = ua.match(/Edg(e|A)\/([0-9.]+)/);
    if (match) return match[2];
  }

  return null;
};

// Parse OS version from userAgent
const parseOsVersion = () => {
  if (typeof navigator === "undefined") {
    return null;
  }

  const ua = navigator.userAgent || "";

  // Windows
  if (/Windows NT/.test(ua)) {
    const match = ua.match(/Windows NT ([0-9.]+)/);
    if (match) {
      const ver = match[1];
      // Map Windows NT versions to marketing names
      const versions = {
        "10.0": "Windows 10/11",
        "6.3": "Windows 8.1",
        "6.2": "Windows 8",
      };
      return versions[ver] || `Windows NT ${ver}`;
    }
  }

  // macOS
  if (/Mac OS X/.test(ua)) {
    const match = ua.match(/Mac OS X ([0-9_.]+)/);
    if (match) return `macOS ${match[1].replace(/_/g, ".")}`;
  }

  // iOS
  if (/iPhone OS|iPad OS/.test(ua)) {
    const match = ua.match(/(?:iPhone OS|iPad OS) ([0-9_]+)/);
    if (match) return `iOS ${match[1].replace(/_/g, ".")}`;
  }

  // Android
  if (/Android/.test(ua)) {
    const match = ua.match(/Android ([0-9.]+)/);
    if (match) return `Android ${match[1]}`;
  }

  // Linux
  if (/Linux/.test(ua)) {
    const match = ua.match(/Linux ([0-9.]*)/);
    return match ? `Linux ${match[1]}` : "Linux";
  }

  return null;
};

// Parse device model from userAgent
const parseDeviceModel = () => {
  if (typeof navigator === "undefined") {
    return null;
  }

  const ua = navigator.userAgent || "";

  // iPhone models
  if (/iPhone/.test(ua)) {
    const match = ua.match(/iPhone(\s)?(\d+(?:,\d+)?)/);
    if (match) return `iPhone ${match[2]}`;
    return "iPhone";
  }

  // iPad models
  if (/iPad/.test(ua)) {
    const match = ua.match(/iPad(\s)?(\d+(?:,\d+)?)/);
    if (match) return `iPad ${match[2]}`;
    // Check for iPad Pro, Air, Mini
    if (/iPad Pro/.test(ua)) return "iPad Pro";
    if (/iPad Air/.test(ua)) return "iPad Air";
    if (/iPad mini/.test(ua)) return "iPad mini";
    return "iPad";
  }

  // Android devices (usually manufacturer + model)
  if (/Android/.test(ua)) {
    // Extract device model component
    const buildMatch = ua.match(/Android[\s;]([^;)]*)/);
    if (buildMatch) {
      return buildMatch[1].trim() || null;
    }
  }

  // Samsung devices
  if (/Samsung/.test(ua)) {
    const match = ua.match(/Samsung[;\s]+([^;)]+)/);
    if (match) return `Samsung ${match[1]}`;
  }

  return null;
};

// Determine device type: mobile, tablet, or desktop
const getDeviceType = () => {
  if (typeof navigator === "undefined") {
    return "unknown";
  }

  const ua = navigator.userAgent || "";

  // Tablet detection
  if (/iPad|Android[\s;]|Tablet|tablet/.test(ua) && !/Mobile|Android 4|Nexus 7|Nexus 10/.test(ua)) {
    return "tablet";
  }

  // Mobile detection
  if (/iPhone|Android|Mobile|mobile|BlackBerry|IEMobile|Opera Mini|Windows Phone/.test(ua)) {
    return "mobile";
  }

  return "desktop";
};

// Get CPU architecture info
const getCpuArchitecture = () => {
  if (typeof navigator === "undefined") {
    return null;
  }

  const ua = navigator.userAgent || "";

  if (/x86_64|x64|Win64|WOW64/.test(ua)) return "x64";
  if (/x86|Win32|i[3-6]86/.test(ua)) return "x86";
  if (/ARM64|aarch64/.test(ua)) return "arm64";
  if (/ARM|Mobile|Android/.test(ua)) return "arm";

  return null;
};

export const collectDeviceInfo = () => {
  if (typeof window === "undefined") {
    return {
      // Basic device identification
      browserName: browserName || "Unknown",
      browserVersion: null,
      deviceType: "unknown",
      deviceModel: null,
      osName: osName || "Unknown",
      osVersion: null,
      cpuArchitecture: null,

      // Capabilities
      isMobile,
      hasTouch: false,
      touchPoints: 0,

      // Hardware specs
      deviceMemory: null,
      hardwareConcurrency: null,

      // Screen metrics
      screen: null,

      // Additional info
      userAgent: null,
      vendor: null,
      maxTouchPoints: 0,
    };
  }

  const ua = navigator.userAgent || "";
  const maxTouchPoints = navigator.maxTouchPoints || navigator.msMaxTouchPoints || 0;

  return {
    // Basic device identification
    browserName: browserName || "Unknown",
    browserVersion: parseBrowserVersion(),
    deviceType: getDeviceType(),
    deviceModel: parseDeviceModel(),
    osName: osName || "Unknown",
    osVersion: parseOsVersion(),
    cpuArchitecture: getCpuArchitecture(),

    // Capabilities
    isMobile,
    hasTouch:
      "ontouchstart" in window ||
      maxTouchPoints > 0 ||
      (navigator.msMaxTouchPoints || 0) > 0,
    touchPoints: maxTouchPoints,

    // Hardware specs
    deviceMemory: navigator.deviceMemory || null,
    hardwareConcurrency: navigator.hardwareConcurrency || null,

    // Screen metrics — detailed viewport and resolution info
    screen: {
      width: window.screen?.width || null,
      height: window.screen?.height || null,
      pixelRatio: window.devicePixelRatio || 1,
      viewportWidth: window.innerWidth || null,
      viewportHeight: window.innerHeight || null,
      colorDepth: window.screen?.colorDepth || null,
      pixelDepth: window.screen?.pixelDepth || null,
    },

    // Additional info for Datadog-like inventory
    userAgent: ua || null,
    vendor: navigator.vendor || null,
    maxTouchPoints,
  };
};

const getDisplayMode = () => {
  if (typeof window === "undefined") {
    return "browser";
  }

  if (window.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  }

  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }

  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return "minimal-ui";
  }

  return "browser";
};

export const collectEnvironmentInfo = () => {
  if (typeof window === "undefined") {
    return {
      timezone: null,
      locale: null,
      referrer: null,
      displayMode: "browser",
      isPwaInstalled: false,
      darkMode: false,
    };
  }

  const displayMode = getDisplayMode();
  return {
    timezone: Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || null,
    locale: navigator.language || null,
    referrer: document.referrer || null,
    displayMode,
    isPwaInstalled: displayMode !== "browser",
    darkMode: window.matchMedia("(prefers-color-scheme: dark)").matches,
  };
};

export const collectNetworkInfo = () => {
  if (typeof navigator === "undefined") {
    return {
      online: true,
      connectionType: "unknown",
      effectiveType: "unknown",
      rtt: null,
      downlink: null,
      saveData: false,
    };
  }

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return {
    online: navigator.onLine,
    connectionType: connection?.type || "unknown",
    effectiveType: connection?.effectiveType || "unknown",
    rtt: connection?.rtt || null,
    downlink: connection?.downlink || null,
    saveData: Boolean(connection?.saveData),
  };
};

export const collectJwtInfo = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return {
      hasToken: Boolean(token),
      exp: null,
      expiresAtIso: null,
      timeToExpiryMs: null,
      isExpired: null,
    };
  }

  const expiresAtMs = payload.exp * 1000;
  const timeToExpiryMs = expiresAtMs - Date.now();

  return {
    hasToken: true,
    exp: payload.exp,
    expiresAtIso: new Date(expiresAtMs).toISOString(),
    timeToExpiryMs,
    isExpired: timeToExpiryMs <= 0,
  };
};
